import { GapCursor } from '@tiptap/pm/gapcursor';
import type { Node as PMNode, ResolvedPos } from '@tiptap/pm/model';
import { NodeSelection, Plugin, PluginKey, Selection, TextSelection } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

import { TEMPLATE_FIELD_BLOCK_NAME, TEMPLATE_FIELD_INLINE_NAME } from '../../doc-engine/templateFields';
import {
  getFormFieldExitPos,
  isAtFieldEdge,
  isBodyBlockParent,
  isFieldInCard,
  TDirection,
} from '../nodes/formField/formFieldHelpers';
import { FORM_FIELD_LINE_NODE_NAME, FORM_FIELD_NODE_NAME } from '../nodes/formField/constants';

type TArrow = { dir: TDirection; isVertical: boolean };

const ARROWS: Partial<Record<string, TArrow>> = {
  ArrowLeft: { dir: -1, isVertical: false },
  ArrowRight: { dir: 1, isVertical: false },
  ArrowUp: { dir: -1, isVertical: true },
  ArrowDown: { dir: 1, isVertical: true },
};

type TArrowStart = {
  // Where the step to another block starts: past the textblock edge or the selected card, or beside a selected node.
  pos: number;
  // The end of the card the selection is in or on.
  cardEnd: number | undefined;
};

const isInLineField = ($caret: ResolvedPos): boolean =>
  $caret.parent.type.name === TEMPLATE_FIELD_INLINE_NAME && $caret.node(-1).type.name === FORM_FIELD_LINE_NODE_NAME;

/** The depth of the textblock whose `dir` edge the caret is at; on a card's line, the edge of its Inline text field. */
const getEdgeDepth = ($caret: ResolvedPos, dir: TDirection): number | undefined => {
  const { parent, parentOffset } = $caret;
  if (parent.isTextblock) return parentOffset === (dir > 0 ? parent.content.size : 0) ? $caret.depth : undefined;
  return isInLineField($caret) && isAtFieldEdge($caret, dir) ? $caret.depth - 1 : undefined;
};

// A card's line lays its field out as one box, which ProseMirror's check reads as one line however the answer wraps.
const isOnFieldEdgeLine = (view: EditorView, $caret: ResolvedPos, dir: TDirection): boolean => {
  const caret = view.coordsAtPos($caret.pos);
  const edge = view.coordsAtPos(dir > 0 ? $caret.end() : $caret.start());
  const middle = (caret.top + caret.bottom) / 2;
  return middle > edge.top && middle < edge.bottom;
};

/** Whether a caret is on a card's line: in its Inline text field, or beside its field. */
const isOnCardLine = ($caret: ResolvedPos): boolean =>
  isInLineField($caret) || $caret.parent.type.name === FORM_FIELD_LINE_NODE_NAME;

// The document, a landscape section and a Content text answer stack their blocks, which up and down move between.
const isStackParent = (node: PMNode): boolean =>
  isBodyBlockParent(node) || node.type.name === TEMPLATE_FIELD_BLOCK_NAME;

/** Where up or down leaves the stacked block around a caret on its textblock's first or last line. */
const getStackedBlockEdge = (view: EditorView, $caret: ResolvedPos, dir: TDirection): number | undefined => {
  if (!view.endOfTextblock(dir > 0 ? 'down' : 'up')) return undefined;
  for (let depth = $caret.parent.isTextblock ? $caret.depth : $caret.depth - 1; ; depth--) {
    const parent = $caret.node(depth - 1);
    if (isStackParent(parent)) return dir > 0 ? $caret.after(depth) : $caret.before(depth);
    const isEdgeChild = $caret.index(depth - 1) === (dir > 0 ? parent.childCount - 1 : 0);
    // A table row's cells sit side by side, so up or down leaves the row from any of them.
    if (!isEdgeChild && parent.type.spec.tableRole !== 'row') return undefined;
  }
};

/** Where up or down steps to another block from: past a card's line, or past the stacked block at the caret's edge. */
const getLineEdgeStep = (view: EditorView, $caret: ResolvedPos, dir: TDirection): number | undefined => {
  if (!isOnCardLine($caret)) return getStackedBlockEdge(view, $caret, dir);
  if (isInLineField($caret) && !isOnFieldEdgeLine(view, $caret, dir)) return undefined;
  const lineDepth = isInLineField($caret) ? $caret.depth - 1 : $caret.depth;
  return dir > 0 ? $caret.after(lineDepth) : $caret.before(lineDepth);
};

/** The end of a selected card, or of the card around a selected field; a node selected inside an answer has none. */
const getSelectedCardEnd = ({ node, from, to, $from }: NodeSelection): number | undefined => {
  if (node.type.name === FORM_FIELD_NODE_NAME) return to;
  return isFieldInCard($from.doc, from) ? getFormFieldExitPos($from) : undefined;
};

/** Where an arrow steps to another block from: a caret at a textblock's edge, a selected node or a gap cursor. */
const getArrowStart = (view: EditorView, { dir, isVertical }: TArrow): TArrowStart | undefined => {
  const { selection } = view.state;
  const { $from } = selection;
  if (selection instanceof NodeSelection || selection instanceof GapCursor) {
    // Past a selected card or card field; from the side of any other selected node or of a gap cursor.
    const selectedCardEnd = selection instanceof NodeSelection ? getSelectedCardEnd(selection) : undefined;
    if (selectedCardEnd === undefined) {
      return { pos: dir > 0 ? selection.to : selection.from, cardEnd: getFormFieldExitPos($from) };
    }
    const cardStart = selectedCardEnd - $from.doc.resolve(selectedCardEnd).nodeBefore!.nodeSize;
    return { pos: dir > 0 ? selectedCardEnd : cardStart, cardEnd: selectedCardEnd };
  }
  if (!(selection instanceof TextSelection) || !selection.empty) return undefined;
  const cardEnd = getFormFieldExitPos($from);
  if (isVertical) {
    const pos = getLineEdgeStep(view, $from, dir);
    return pos === undefined ? undefined : { pos, cardEnd };
  }
  const depth = getEdgeDepth($from, dir);
  return depth === undefined ? undefined : { pos: dir > 0 ? $from.after(depth) : $from.before(depth), cardEnd };
};

/** On a card's line, where a step lands, the field instead: an Inline text field's text, or the Dropdown selected. */
const enterLineField = (doc: PMNode, found: Selection, dir: TDirection): Selection | undefined => {
  if (found.$from.parent.type.name !== FORM_FIELD_LINE_NODE_NAME) return undefined;
  const field = dir > 0 ? found.$from.nodeAfter : found.$from.nodeBefore;
  if (!field) return undefined;
  if (field.type.name === TEMPLATE_FIELD_INLINE_NAME) return TextSelection.create(doc, found.from + dir);
  return NodeSelection.create(doc, dir > 0 ? found.from : found.from - field.nodeSize);
};

// Without it, an arrow stops inside a card's markup, beside its Dropdown or between two cards.
const getFormFieldArrowPlugin = (): Plugin =>
  new Plugin({
    key: new PluginKey('formFieldArrow'),
    props: {
      handleKeyDown(view, event) {
        const arrow = ARROWS[event.key];
        if (!arrow || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return false;
        const start = getArrowStart(view, arrow);
        if (!start) return false;

        // ProseMirror's own step to the next block.
        const found = Selection.findFrom(view.state.doc.resolve(start.pos), arrow.dir);
        // Nothing past a last card: stay, where the browser would jump back.
        if (!found) return arrow.dir > 0 && start.cardEnd !== undefined;
        // Between two blocks outside cards, or within one card's answer, the arrow stays the browser's.
        if (getFormFieldExitPos(found.$from) === start.cardEnd) return false;
        const lineField = enterLineField(view.state.doc, found, arrow.dir);
        // Up and down keep the browser's column, except to or from a card's line, where the browser misses the field.
        const { selection } = view.state;
        if (arrow.isVertical && selection instanceof TextSelection && !lineField && !isOnCardLine(selection.$from)) {
          return false;
        }
        view.dispatch(view.state.tr.setSelection(lineField ?? found).scrollIntoView());
        return true;
      },
    },
  });

export default getFormFieldArrowPlugin;
